import { useMemo } from 'react';

import isNil from 'lodash/isNil';

import { GLOBAL, useTranslate } from '@lumapps/translations';

/** The base props required to build a search results live status message */
type BaseProps = {
    /**
     * The query that has been searched.
     * */
    query?: string;
    /** The current loading state */
    isLoading?: boolean;
    /** The current error state. */
    hasError?: boolean;
};

/**
 * The props needed if the number of results should be read out.
 * The currently displayed results have to be set in order to also set the total results.
 */
type PropsWithResultsCount = BaseProps & {
    /**
     * The number of results that are currently being displayed
     * This should **not** be to the total number of items.
     */
    displayedResultsLength: number;
    /**
     * The total number of results that could be displayed in the current list
     * This should  be to the total number of items.
     */
    totalResultsLength?: number;
};

/**
 * The props needed if the number of results shouldn't or can't be read out.
 */
type PropsWithoutResultsCount = BaseProps & {
    displayedResultsLength?: never;
    totalResultsLength?: never;
};

/** Props for useSearchResultsLiveStatusMessageText */
export type SearchResultsLiveStatusMessageProps = PropsWithResultsCount | PropsWithoutResultsCount;

/**
 * Build a status message for an ongoing query.
 *
 * This message will be read by screen readers to convey the current status of a search.
 * Add as much information you can when possible, especially if they are also available visually.
 *
 * Ex: If you display somewhere "X results for query", add the query and totalResultsLength props.
 * The displayedResultsLength is also required if totalResultsLength is given in order to form a complete sentence.
 * The resulting screen reader message will be `X results displayed out of Y for query`.
 *
 * * Loading
 * * Error
 * * %NB% results displayed for %query%
 * * %NB% results displayed out of %TOTAL% for %query%
 * or if the query is not known
 * * %NB results% displayed
 * * %NB% results displayed out of %TOTAL%
 */
export const useSearchResultsLiveStatusMessageText = ({
    query,
    displayedResultsLength,
    totalResultsLength,
    isLoading,
    hasError,
}: SearchResultsLiveStatusMessageProps): string => {
    const { translateKey, translateAndReplace, pluralize } = useTranslate();

    const message = useMemo(() => {
        if (isLoading) {
            return translateKey(GLOBAL.LOADING);
        }

        if (hasError) {
            return translateKey(GLOBAL.ERROR);
        }

        if (isNil(displayedResultsLength)) {
            return '';
        }

        let translationKey;
        // X results out of Y displayed for QUERY
        if (query && totalResultsLength) {
            translationKey = translateAndReplace(GLOBAL.NB_RESULTS_DISPLAYED_OUT_OF_TOTAL_FOR_QUERY, {
                NB: displayedResultsLength,
                TOTAL: totalResultsLength,
                QUERY: query,
            });
            // X results out of Y displayed
        } else if (totalResultsLength) {
            translationKey = translateAndReplace(GLOBAL.NB_RESULTS_DISPLAYED_OUT_OF_TOTAL, {
                NB: displayedResultsLength,
                TOTAL: totalResultsLength,
            });
            // X results displayed for QUERY
        } else if (query) {
            translationKey = translateAndReplace(GLOBAL.NB_RESULTS_DISPLAYED_FOR_QUERY, {
                NB: displayedResultsLength,
                query,
            });
            // X results displayed
        } else {
            translationKey = translateAndReplace(GLOBAL.NB_RESULTS_DISPLAYED, {
                NB: displayedResultsLength,
            });
        }

        return pluralize(translationKey, displayedResultsLength);
    }, [
        isLoading,
        hasError,
        displayedResultsLength,
        translateKey,
        query,
        totalResultsLength,
        pluralize,
        translateAndReplace,
    ]);

    return message;
};
